import { BODY_ACTIONS, type BodyAction } from '@d2/schema';

import {
  ACTION_BURST,
  ACTION_DUST,
  ACTION_GATHER,
  ACTION_GUARD,
  ACTION_MARK_RISE,
  ACTION_MOTES,
  ACTION_POOL,
  ACTION_SLASH,
  ACTION_TEXTURE_PREFIX,
  CORPSE_ANGLE,
  GROUND_MARK_RISE,
  PALETTE,
} from './art-spec';

/**
 * What a body **doing something** looks like — decided here, drawn elsewhere,
 * and Phaser-free so both halves can be tested without a browser.
 *
 * `monster-art.ts` and `ground-art.ts`'s counterpart for the fifth thing the
 * wire says about a body, and the same division of labour: `art-spec.ts` holds
 * the numbers and the colours, `placeholder-art.ts` turns them into textures,
 * and this is the mapping from *what the wire says a body is doing* to *how that
 * is drawn*.
 *
 * ## No rule crosses this file either
 *
 * `BodyAction` is `@d2/schema`'s vocabulary and `bodyActionOf` is its reader
 * (`./wire`), so what reaches here is a name the server chose from a list the
 * wire owns. Nothing here decides when a body is striking, how long a swing
 * lasts, or what a blow costs — all of that is `@d2/core`'s, applied by the
 * room, and a client that decided any of it would be the duplicated rule this
 * architecture exists to prevent.
 *
 * **In particular, this module still does not invent a wind-up — it draws one.**
 * What stood here was a refusal: the obvious thing to draw is a body *about* to
 * swing, there was no such state, a monster's blow landed on the first tick of
 * its animation, and an anticipation drawn during the cooldown would have
 * promised a player reaction time the rules did not give them (`TODO.md` #483).
 * Core now carries D2's **ActionFlag** — the frame of an attack animation the
 * blow actually lands on — so a swing has a real, timed, server-side wind-up
 * (`attackWindUpTicks`; the neutral profile is a twelve-tick swing whose blow
 * lands on tick seven, 350 ms of 600), `apps/game` derives the eighth code from
 * core's own `windingUp`, and the mark below telegraphs a window the simulation
 * genuinely holds open: a melee monster closes at 3 cells/s against a character's
 * 4, so a player who steps away inside it is not hit. The vocabulary this module
 * draws is still exactly `BODY_ACTIONS` and never a state of its own —
 * `action-art.test.ts` asserts it — and the cast half of #483 is still refused
 * on the old grounds, because a cast animation's own ActionFlag is a number core
 * does not carry.
 *
 * ## The one judgement this module makes: which band a mark is drawn in
 *
 * A body already answers four questions in four places, and each has a reader
 * aimed at it — the ring under the feet is the elite tier, the middle of the
 * body is the archetype, the crown is the boss, the bar above it is the life
 * left. **An action is therefore drawn as a mark of its own, in the one band
 * nothing had claimed**, and never as a recolouring of the body: a hurt monster
 * that flashed white would stop reading as a caster, and an elite that flinched
 * would stop reading as an elite.
 *
 * So the channels stay separable by *geometry*, not by good intentions:
 *
 *   | band                       | says                    |
 *   |----------------------------|-------------------------|
 *   | above the head             | life, and a boss's crown |
 *   | the shoulders              | **what it is doing**    |
 *   | the middle of the body     | the archetype           |
 *   | the margin beside the feet | the elite tier          |
 *
 * `action-art.test.ts` pins all three separations numerically, on a monster and
 * on the taller player capsule, so the day a size changes the arithmetic fails
 * rather than the pixels quietly overlapping.
 *
 * ## Two of the eight are not reachable in a running game, and are drawn anyway
 *
 * `blocking` needs a block rating no stat block on the floor carries and a lock
 * no rule spends frames on (`TODO.md` #32); `casting` is a delay that is zero
 * for eight of the nine skills in the catalogue (`TODO.md` #483). Both are real
 * codes derived from real rolls, so both are drawn — the art is ready the day
 * the rule lands — and neither is asserted against a server, because a test that
 * required one would be a test of something no room can produce.
 */

/** The silhouette a mark is drawn as. `placeholder-art.ts` owns what each one means in pixels. */
export type MarkShape = 'slash' | 'motes' | 'burst' | 'guard' | 'dust' | 'pool' | 'gather';

/** One mark: what it is drawn as, in what colour, at what size, and how high above the feet. */
export interface ActionMark {
  /** The action this mark says. */
  readonly action: BodyAction;
  /** The texture key. Derived from the action, so nothing keeps a table of names. */
  readonly key: string;
  readonly shape: MarkShape;
  readonly colour: number;
  readonly size: { readonly width: number; readonly height: number };
  /** How far above the feet the mark's middle sits, as a fraction of the body's own height. */
  readonly rise: number;
}

/** Everything an action does to how a body is drawn. */
export interface ActionLook {
  readonly action: BodyAction;
  /** The mark drawn over the body, or `null` for a body doing nothing worth saying. */
  readonly mark: ActionMark | null;
  /**
   * The angle the body itself is drawn at, in degrees.
   *
   * Zero for everything but a corpse. **A body is drawn on its own feet
   * whatever it is doing**, because `targeting.ts` hit-tests it from its
   * *position*: a sprite nudged for a lunge would make the pixels lie about
   * what a click would land on, which is a monster the player can see and
   * cannot hit.
   */
  readonly angle: number;
}

/** What a mark is, before it knows which action it belongs to. */
interface MarkSpec {
  readonly shape: MarkShape;
  readonly colour: number;
  readonly size: { readonly width: number; readonly height: number };
  readonly rise: number;
}

/**
 * The whole vocabulary, as a total map over the wire's own list.
 *
 * A `Record<BodyAction, …>` rather than a lookup that can answer "never heard of
 * it": `BODY_ACTIONS` is append-only and this is the client's half of it, so the
 * day the wire grows a code this file stops compiling instead of silently
 * drawing nothing. That is the same discipline `dungeonParams` keeps for a
 * dungeon option — and it is no longer hypothetical. This comment used to name
 * `winding-up` as the code that would one day arrive; it arrived, `apps/web`
 * stopped compiling with one error naming the missing key, and the eighth entry
 * below is what closing it looked like.
 */
const MARKS: Readonly<Record<BodyAction, MarkSpec | null>> = {
  /**
   * Nothing at all. Idle is the code sixty bodies spawn carrying and the one a
   * standing body holds for most of its life, so it costs nothing on screen for
   * the same reason `IDLE_ACTION` costs nothing on the wire.
   */
  idle: null,
  walking: { shape: 'dust', colour: PALETTE.actionWalk, size: ACTION_DUST, rise: GROUND_MARK_RISE },
  striking: { shape: 'slash', colour: PALETTE.actionStrike, size: ACTION_SLASH, rise: ACTION_MARK_RISE },
  /**
   * **A blow being gathered**: the swing's own lens, halved and folded back into
   * the body, in the swing's own hue one heat cooler.
   *
   * Deliberately the *same* mark as `striking` seen a few hundred milliseconds
   * earlier, and deliberately not mistakable for it. Same band — the shoulders,
   * so the eye that caught the warning is already looking where the blow will be
   * drawn. Same family of silhouette — the two wedges here are the halves of the
   * lens the strike draws whole, points still meeting inside the body instead of
   * running out both sides of it. Same fire, one temperature down. What differs
   * is the two things a glance reads first: it is **half the width**, and it
   * stops at the body's own edges rather than breaking them.
   *
   * That asymmetry is the feature. `striking` is the widest mark there is
   * because a broken silhouette is what the eye finds among sixty bodies, and it
   * has to stay that way: a wind-up runs for seven of every twelve ticks of a
   * neutral swing against the blow's five, so on a floor in melee this is the
   * mark most bodies are wearing most of the time. Drawn as loudly as the strike
   * it would hide the one body that has already committed — which is the
   * `ACTION_DUST` argument again, made about the busiest state of a fight rather
   * than about walking.
   *
   * It is a telegraph a player can act on, and that is the whole reason it is
   * allowed to exist: D2 documents an attack as interruptable until the frame
   * carrying the ActionFlag plays, `apps/game` lands the blow on that tick and
   * whiffs it if the target has moved out of reach, and a melee monster walks at
   * 3 cells/s against a character's 4. The window is real, and this is what makes
   * it usable rather than theoretical.
   *
   * Written here, beside the swing it belongs to, rather than last where the
   * wire keeps it. `BODY_ACTIONS` had to append the code because a wire value is
   * a number and re-slotting one re-labels every deployed body; this map is keyed
   * by name and read by people, so the two halves of one animation sit together.
   */
  'winding-up': { shape: 'gather', colour: PALETTE.actionWindUp, size: ACTION_GATHER, rise: ACTION_MARK_RISE },
  blocking: { shape: 'guard', colour: PALETTE.actionBlock, size: ACTION_GUARD, rise: ACTION_MARK_RISE },
  hurt: { shape: 'burst', colour: PALETTE.actionHurt, size: ACTION_BURST, rise: ACTION_MARK_RISE },
  casting: { shape: 'motes', colour: PALETTE.actionCast, size: ACTION_MOTES, rise: ACTION_MARK_RISE },
  /**
   * A corpse: blood on the ground, and the body turned a quarter over onto it.
   *
   * The one action that touches the body itself, and the one that has to survive
   * being looked at rather than watched — a seat is dead for the few seconds
   * between the killing blow and the **run ending**, and *"is that a body or a
   * corpse"* has to be answerable from across the floor. A mark alone could not
   * do that; a silhouette lying down can.
   *
   * That wait no longer ends in the corpse standing up. It ends in the seat
   * leaving (`descent.room.ts`'s `endRun`, close code 4200), so this is the last
   * thing anybody sees of that body — the party's other seats included, since it
   * stops being replicated the moment the run ends.
   *
   * Only a **player** ever wears it. A monster's death is a despawn, so the code
   * never travels for one (`setMonsterAction` refuses it outright), which is
   * also why a ground-level mark here can never meet an elite's ring.
   */
  dead: { shape: 'pool', colour: PALETTE.actionDead, size: ACTION_POOL, rise: GROUND_MARK_RISE },
};

/** The texture key for an action's mark. One name, derived, never a second table. */
export const actionTexture = (action: BodyAction): string => `${ACTION_TEXTURE_PREFIX}${action}`;

const lookOf = (action: BodyAction): ActionLook => {
  const spec = MARKS[action];

  return Object.freeze({
    action,
    mark: spec === null ? null : Object.freeze({ action, key: actionTexture(action), ...spec }),
    angle: action === 'dead' ? CORPSE_ANGLE : 0,
  });
};

/**
 * Every look there is, built once.
 *
 * Eight of them, baked at module load and shared by however many bodies the room
 * spawns — the count answers to the *vocabulary*, never to the population, which
 * is the same bargain `placeholder-art.ts` strikes with its twelve monster
 * textures. It also means `actionLook` allocates nothing: sixty bodies at sixty
 * frames a second would otherwise be 3600 throwaway objects a second for a value
 * that never changes.
 */
const LOOKS: Readonly<Record<BodyAction, ActionLook>> = Object.freeze(
  Object.fromEntries(BODY_ACTIONS.map((action) => [action, lookOf(action)])) as Record<BodyAction, ActionLook>,
);

/**
 * How one body is drawn, from what the wire said it is doing.
 *
 * `undefined` is idle, and that is the wire's own answer rather than a
 * convenience: `IDLE_ACTION` is zero because *"a body that nothing has described
 * yet is standing there doing nothing"* (`packages/schema/src/actions.ts`), and
 * a body this client is drawing without ever having been told about — one the
 * renderer sandbox stood on the floor — is the same case. Note what this is
 * **not**: a fallback for a code the wire could not name. That one throws, at
 * the edge, in `bodyActionOf`, because guessing `idle` there would draw the
 * swinging monster standing still.
 */
export const actionLook = (action: BodyAction | undefined): ActionLook => LOOKS[action ?? 'idle'];

/** Every mark that has to be baked into a texture — one per action that has one. */
export const ACTION_MARKS: readonly ActionMark[] = Object.freeze(
  BODY_ACTIONS.flatMap((action: BodyAction) => {
    const mark = LOOKS[action].mark;

    return mark === null ? [] : [mark];
  }),
);

/**
 * The texture a body's mark sprite is created with, before anything has been
 * said about that body.
 *
 * A Phaser image has to be created with *some* texture and an idle body carries
 * no mark at all, so the sprite is created hidden and the first action with
 * something to say sets its own. Which one this is therefore does not matter —
 * `striking` only because it is the one a body in a fight needs first.
 */
export const RESTING_MARK_TEXTURE = actionTexture('striking');

/**
 * How far above a body's feet its mark is drawn, in pixels.
 *
 * The body's own height, because a monster is 44 pixels and a player 56 and the
 * mark belongs on the shoulders of each rather than at a height that happens to
 * suit one of them. A ground mark's rise is zero, so this answers zero for it
 * whatever the body — blood lies on the floor, not on a scaled fraction of one.
 */
export const markRise = (mark: ActionMark, bodyHeight: number): number => mark.rise * bodyHeight;
